import { keepPreviousData, queryOptions, skipToken } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import { AuthError } from '@/lib/api/auth-error'
import type { components, paths } from '@/lib/api/schema'
import { navigateTo } from '@/lib/navigate'
import { apiUrl } from '@/lib/api/url'

export type PlanKey = 'starter' | 'growth' | 'professional' | 'business'
export const PLAN_KEYS: readonly PlanKey[] = ['starter', 'growth', 'professional', 'business']

/** A `?plan=` from the URL, if it names a plan we sell; anything else is ignored (the API defaults). */
export function planFromParam(value: string | null): PlanKey | undefined {
  return PLAN_KEYS.find((key) => key === value)
}

/** One query-options factory per resource; pages never call `api` directly. */
export const healthQuery = queryOptions({
  queryKey: ['health'],
  queryFn: async () => {
    const { data, error } = await api.GET('/api/healthz')
    if (error || !data) throw new Error('API unreachable')
    return data
  },
  refetchInterval: 30_000,
})

/**
 * The signed-in user and the accounts they may enter (FR-22c).
 *
 * 401 is a normal answer, not a failure: it means "not signed in". Throwing
 * would make every guarded route flash an error boundary on first load, so it
 * resolves to null and the guard redirects.
 */
export const sessionQuery = queryOptions({
  queryKey: ['session'],
  queryFn: async () => {
    const { data, response } = await api.GET('/api/auth/session')
    if (response.status === 401) return null
    if (!data) throw new Error('Could not load your session')
    return data
  },
  // Recover on its own. A 401 is returned as null rather than thrown, so a retry
  // here only ever follows a genuine failure -- an API still starting, a dev
  // server restarting, a dropped network. Without this, one unlucky second
  // leaves the console stuck on "Can't reach Ringout" for the life of the page,
  // because the error is cached and nothing would ask again.
  retry: 2,
  retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
  // Both override the global defaults deliberately: coming back to the tab, or
  // regaining the network, is exactly when a stale failure should be re-checked.
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
  staleTime: 60_000,
})

export type SignupBody = {
  email: string
  full_name: string
  password: string
  checkout_session_id?: string
  plan?: PlanKey
}
export type LoginBody = { email: string; password: string; checkout_session_id?: string }

/** Field-level messages the API can return, mapped to something a person can act on. */
export function authErrorMessage(code: string | undefined, status: number): string {
  if (code === 'email_already_registered')
    return 'That email already has a RingOut account. Try signing in instead.'
  if (code === 'invalid_credentials') return 'That email and password don’t match.'
  if (status === 422) return 'Check the details above and try again.'
  return 'Something went wrong. Please try again.'
}

export async function signup(body: SignupBody) {
  const { data, error, response } = await api.POST('/api/auth/signup', { body })
  if (!data) {
    const detail = (error as { error?: { code?: string; message?: string } } | undefined)?.error
    // A weak password comes with the reason ("repeats the same few characters", "contains your
    // email"): say it, because "check the details" leaves a person guessing what to change.
    const message =
      detail?.code === 'password_too_weak' && detail.message
        ? detail.message
        : authErrorMessage(detail?.code, response.status)
    throw new AuthError(message, detail?.code)
  }
  return data
}

export async function login(body: LoginBody) {
  const { data, error, response } = await api.POST('/api/auth/login', { body })
  if (!data) {
    const code = (error as { error?: { code?: string } } | undefined)?.error?.code
    throw new AuthError(authErrorMessage(code, response.status), code)
  }
  return data
}

export type VerifyErrorCode =
  | 'invalid_code'
  | 'code_expired'
  | 'too_many_attempts'
  | 'code_sent_recently'
  | 'email_already_verified'

/** Each one tells the person something different about what to do next. */
export function verifyErrorMessage(code: string | undefined): string {
  switch (code) {
    case 'invalid_code':
      return 'That code isn’t right. Check the email and try again.'
    case 'code_expired':
      return 'That code has expired. Send yourself a new one.'
    case 'too_many_attempts':
      return 'Too many attempts. Send yourself a new code to start again.'
    case 'code_sent_recently':
      return 'A code was just sent. Give it a minute before asking for another.'
    case 'email_already_verified':
      return 'That email is already verified.'
    default:
      return 'Something went wrong. Please try again.'
  }
}

export async function verifyEmail(code: string) {
  const { data, error } = await api.POST('/api/auth/verify-email', { body: { code } })
  if (!data) {
    const c = (error as { error?: { code?: string } } | undefined)?.error?.code
    throw new Error(verifyErrorMessage(c))
  }
  return data
}

export async function resendVerification() {
  const { data, error } = await api.POST('/api/auth/verify-email/send')
  if (!data) {
    const c = (error as { error?: { code?: string } } | undefined)?.error?.code
    throw new Error(verifyErrorMessage(c))
  }
  return data
}

export type Locality = components['schemas']['Locality']

/** Shorter than this, a suburb search matches too much to be worth a request. */
export const LOCALITY_MIN_QUERY = 2

/**
 * Suburbs, states and postcodes for the onboarding picker, from the region's list.
 * The list changes once a quarter, so a search is never refetched; the previous
 * results stay up while the next keystroke's arrive.
 */
export const localitiesQuery = (q: string) =>
  queryOptions({
    queryKey: ['localities', q],
    queryFn: async ({ signal }) => {
      const { data, error } = await api.GET('/api/region/localities', {
        params: { query: { q } },
        signal,
      })
      if (error || !data) throw new Error('Could not search suburbs')
      return data
    },
    enabled: q.trim().length >= LOCALITY_MIN_QUERY,
    staleTime: Infinity,
    placeholderData: keepPreviousData,
  })

export type ProfileBody = components['schemas']['BusinessProfileRequest']

/** A refused save. `param` names the field the API objected to, when there is one. */
export class ProfileSaveError extends Error {
  // Declared, not a parameter property: `erasableSyntaxOnly` allows only types
  // that erase to plain JavaScript.
  readonly param?: string

  constructor(message: string, param?: string) {
    super(message)
    this.param = param
  }
}

/** What the person can do about each refusal, in words rather than codes. */
export function profileErrorMessage(code: string | undefined): string {
  switch (code) {
    case 'postcode_unknown':
      return 'We couldn’t place that postcode. Please check it.'
    case 'locality_unknown':
      return 'Choose your suburb from the list.'
    case 'phone_invalid':
      return 'That doesn’t look like a number we can call.'
    case 'industry_other_required':
      return 'Tell us your industry.'
    case 'business_number_invalid':
      return 'That number doesn’t check out. Use the ABN (11 digits) or ACN (9 digits) on your ASIC record.'
    case 'website_invalid':
      return 'Enter a web address like yourbusiness.com.au.'
    case 'string_too_short':
      return 'Please fill this in.'
    case 'literal_error':
      return 'Please choose one.'
    case 'role_cannot_edit_profile':
      return 'Only an owner or admin can change the business details.'
    case 'email_not_verified':
      return 'Verify your email address first.'
    default:
      return 'Something went wrong. Please try again.'
  }
}

/** The session's account's business profile, as Settings → Workspace edits it. */
export const profileQuery = queryOptions({
  queryKey: ['account', 'profile'],
  queryFn: async () => {
    const { data, error } = await api.GET('/api/account/profile')
    if (error || !data) throw new Error('Could not load the business details')
    return data
  },
})

export async function saveProfile(body: ProfileBody) {
  const { data, error } = await api.PUT('/api/account/profile', { body })
  if (!data) {
    const e = (error as { error?: { code?: string; param?: string } } | undefined)?.error
    throw new ProfileSaveError(profileErrorMessage(e?.code), e?.param)
  }
  return data
}

export type AgentSummary = components['schemas']['AgentSummary']
export type AgentDetail = components['schemas']['AgentDetail']
export type AgentBody = components['schemas']['AgentRequest']
export type VoiceChoice = components['schemas']['VoiceChoice']

/** The session's account's agents. Archived ones are gone from the list. */
export const agentsQuery = queryOptions({
  queryKey: ['agents'],
  queryFn: async () => {
    const { data, error } = await api.GET('/api/agents')
    if (error || !data) throw new Error('Could not load your agents')
    return data
  },
})

/**
 * One agent with its Reception Script, or null when there is none to show. The API
 * answers 404 alike for an archived agent and another account's, so this does too.
 */
export const agentQuery = (slug: string) =>
  queryOptions({
    queryKey: ['agents', slug],
    queryFn: async () => {
      const { data, response } = await api.GET('/api/agents/{slug}', { params: { path: { slug } } })
      if (response.status === 404) return null
      if (!data) throw new Error('Could not load this agent')
      return data
    },
  })

/**
 * The region's voices, in the order to offer them. They change with a deploy, not while
 * a page is open. `available` is whether the server's voice service has the voice
 * (null when it has none): one it lacks is tested in the region's fallback voice.
 */
export const voicesQuery = queryOptions({
  queryKey: ['region', 'voices'],
  queryFn: async () => {
    const { data, error } = await api.GET('/api/region/voices')
    if (error || !data) throw new Error('Could not load the voices')
    return data
  },
  staleTime: Infinity,
})

// Checked against the generated schema, so a moved route fails the build rather than the player.
const VOICE_PREVIEW_PATH = '/api/region/voices/{key}/preview' satisfies keyof paths

/**
 * Where a voice's preview plays from: the voice service's own recorded sample, relayed by
 * the API, so it costs no call minutes. For an `<audio>` element's source: the API answers
 * byte ranges, which Safari needs to play media.
 */
export function voicePreviewUrl(key: string) {
  return apiUrl(VOICE_PREVIEW_PATH.replace('{key}', encodeURIComponent(key)))
}

/** A refused save or archive. `param` names the field, as a dotted path, when there is one. */
export class AgentSaveError extends Error {
  readonly param?: string

  constructor(message: string, param?: string) {
    super(message)
    this.param = param
  }
}

const DAY_IN_MESSAGE = /\b(mon|tue|wed|thu|fri|sat|sun):/

/**
 * The API reports a day's rule (two windows at most, in order) against the whole
 * week and names the day in its message. Place it on the day instead.
 */
function agentErrorParam(param: string | undefined, raw: string | undefined) {
  const day = param === 'script.hours' ? raw?.match(DAY_IN_MESSAGE)?.[1] : undefined
  return day ? `${param}.${day}` : param
}

/** What the person can do about each refusal, in words rather than codes. */
export function agentErrorMessage(code: string | undefined, param = '', raw = ''): string {
  switch (code) {
    case 'voice_unknown':
      return 'Choose a voice from the list.'
    case 'role_cannot_edit_agents':
      return 'Only an owner or admin can change agents.'
    case 'agent_not_found':
      return 'This agent no longer exists. It may have been archived.'
    case 'voice_not_chosen':
      return 'Choose a voice first.'
    case 'greeting_missing':
      return 'Write the greeting callers hear first.'
    case 'live_agent_limit':
      // The API's own words carry the plan's number.
      return raw || 'Your plan has no room for another live agent.'
    case 'version_not_found':
      return 'That version no longer exists.'
    case 'transfer_number_invalid':
    case 'transfer_number_loops':
      // The API's words say what is wrong with this number.
      return raw || 'Check this number and try again.'
    case 'email_not_verified':
      return 'Verify your email address first.'
    case 'string_too_short':
    case 'missing':
      if (param === 'name') return 'Give your agent a name.'
      if (param === 'script.greeting') return 'Write the greeting callers hear.'
      if (/^script\.services\.\d+\.name$/.test(param)) return 'Name the service, or remove it.'
      return 'Please fill this in.'
    case 'string_too_long':
      return 'That’s too long. Shorten it and try again.'
    case 'string_pattern_mismatch':
      return 'Enter a time, like 08:00.'
    case 'greater_than_equal':
    case 'less_than_equal':
      return 'Choose a length from 15 minutes to 8 hours.'
    case 'too_long':
      return 'An agent can list up to 30 services.'
    case 'value_error':
      if (param === 'script.services') return 'Each service needs its own name.'
      if (/^script\.hours\.\w+\.\d+$/.test(param)) return 'Closing time must be after opening time.'
      if (/at most two/.test(raw)) return 'A day can have at most two opening windows.'
      if (param.startsWith('script.hours')) return 'The second window must start after the first one closes.'
      return 'Check this and try again.'
    default:
      return 'Something went wrong. Please try again.'
  }
}

function agentSaveError(error: unknown) {
  const e = (error as { error?: { code?: string; param?: string; message?: string } } | undefined)?.error
  const param = agentErrorParam(e?.param, e?.message)
  return new AgentSaveError(agentErrorMessage(e?.code, param, e?.message), param)
}

export async function createAgent(body: AgentBody) {
  const { data, error } = await api.POST('/api/agents', { body })
  if (!data) throw agentSaveError(error)
  return data
}

/** Replaces the agent's name, voice and script. Its slug never changes. */
export async function updateAgent(slug: string, body: AgentBody) {
  const { data, error } = await api.PUT('/api/agents/{slug}', { params: { path: { slug } }, body })
  if (!data) throw agentSaveError(error)
  return data
}

/**
 * Makes the saved draft the next version callers hear; with `version`, makes that earlier
 * version live again (a rollback).
 */
export async function publishAgent(slug: string, version?: number) {
  const { data, error } = await api.POST('/api/agents/{slug}/publish', {
    params: { path: { slug } },
    body: { version: version ?? null },
  })
  if (!data) throw agentSaveError(error)
  return data
}

/** An agent's published versions, newest first, and which one is live. */
export const agentVersionsQuery = (slug: string) =>
  queryOptions({
    queryKey: ['agents', slug, 'versions'],
    queryFn: async () => {
      const { data } = await api.GET('/api/agents/{slug}/versions', { params: { path: { slug } } })
      if (!data) throw new Error('Could not load the versions')
      return data
    },
  })

/** Archives the agent: it stops answering, and it can't be restored. */
export async function archiveAgent(slug: string) {
  const { error, response } = await api.DELETE('/api/agents/{slug}', { params: { path: { slug } } })
  if (!response.ok) throw agentSaveError(error)
}

export type SpeechSessionGrant = components['schemas']['SpeechSessionGrant']
export type VoiceFallback = components['schemas']['VoiceFallback']

/**
 * A refused test session. `message` is for the person; `detail` is the API's own
 * words, kept where they name a cause someone setting up the server can fix.
 */
export class TestSessionError extends Error {
  readonly status: number
  readonly code?: string
  readonly param?: string
  readonly detail?: string

  constructor(message: string, status: number, code?: string, param?: string, detail?: string) {
    super(message)
    this.status = status
    this.code = code
    this.param = param
    this.detail = detail
  }
}

/** What the person can do about each refusal. Some of the API's own messages already say it best. */
export function testSessionErrorMessage(code: string | undefined, raw?: string): string {
  switch (code) {
    case 'speech_provider_not_configured':
      return 'Talking to agents isn’t set up on this server yet.'
    case 'voice_not_chosen':
      return 'Choose a voice for this agent first.'
    case 'voice_unavailable':
      return raw || 'That voice isn’t available yet. Choose another.'
    case 'speech_provider_unavailable':
      return raw || 'The voice service couldn’t start the conversation. Try again in a moment.'
    case 'role_cannot_test_agents':
      return 'Only an owner, admin or receptionist can talk to agents.'
    case 'agent_not_found':
      return 'This agent no longer exists. It may have been archived.'
    case 'email_not_verified':
      return 'Verify your email address first.'
    default:
      return 'Something went wrong. Please try again.'
  }
}

/**
 * Starts a browser conversation with the agent as last saved. The grant is for
 * `lib/speech` alone: its `client` holds a vendor token and, for some vendors, a
 * short-lived key, so it is never cached or logged.
 */
export async function startTestSession(slug: string): Promise<SpeechSessionGrant> {
  const { data, error, response } = await api.POST('/api/agents/{slug}/test-sessions', {
    params: { path: { slug } },
  })
  if (data) return data
  const e = (error as { error?: { code?: string; param?: string; message?: string } } | undefined)?.error
  throw new TestSessionError(testSessionErrorMessage(e?.code, e?.message), response.status, e?.code, e?.param, e?.message)
}

export type TestSessionReport = components['schemas']['SpeechSessionReport']

/**
 * Tells the API how a browser test ended, so the terminal hears what the browser heard --
 * the speech vendor reports "failed to think" to the browser, never to us. Logged, never
 * stored. Fire and forget: the test is over either way, so this never throws.
 */
export async function reportTestSessionEnd(slug: string, sessionId: string, report: TestSessionReport) {
  try {
    await api.POST('/api/agents/{slug}/test-sessions/{session_id}/events', {
      params: { path: { slug, session_id: sessionId } },
      body: report,
    })
  } catch {
    // Nothing to do: the person testing has already been told how it ended.
  }
}

export type BookingItem = components['schemas']['Booking']
export type BookingList = components['schemas']['BookingList']

/** The longest window the bookings list answers for, in whole days. */
export const BOOKINGS_MAX_DAYS = 92

/** A refused read or change. `code` is the API's reason, for a page that treats one differently. */
export class BookingError extends Error {
  readonly status: number
  readonly code?: string

  constructor(message: string, status: number, code?: string) {
    super(message)
    this.status = status
    this.code = code
  }
}

/** What the person can do about each refusal, in words rather than codes. */
export function bookingErrorMessage(code: string | undefined): string {
  switch (code) {
    case 'slot_taken':
      return 'That time is already taken. Choose another.'
    case 'starts_in_past':
      return 'Choose a time that hasn’t passed.'
    case 'booking_cancelled':
      return 'This booking has already been cancelled.'
    case 'not_on_calendar':
      return 'This booking is no longer in the calendar, so it can’t be moved. Mark it cancelled instead.'
    case 'calendar_unavailable':
      return 'The calendar couldn’t be reached, so nothing was changed. Try again.'
    case 'calendar_not_connected':
      return 'The calendar this booking is in isn’t connected, so it can’t be changed here.'
    case 'booking_not_found':
      return 'This booking no longer exists.'
    case 'role_cannot_change_bookings':
      return 'Only an owner, admin or receptionist can change bookings.'
    case 'range_too_long':
      return `Choose a range of ${BOOKINGS_MAX_DAYS} days or fewer.`
    case 'range_invalid':
      return 'The end of the range is before its start.'
    case 'email_not_verified':
      return 'Verify your email address first.'
    default:
      return 'Something went wrong. Please try again.'
  }
}

function bookingError(error: unknown, status: number) {
  const code = (error as { error?: { code?: string } } | undefined)?.error?.code
  return new BookingError(bookingErrorMessage(code), status, code)
}

/**
 * The account's bookings starting on the days `from` to `to` (inclusive, "2026-10-14"),
 * in the account's timezone. Customers' names and numbers come from the calendar on
 * each read; `calendar.error` says when it couldn't be read and they are missing.
 */
export const bookingsQuery = (from: string, to: string) =>
  queryOptions({
    queryKey: ['bookings', from, to],
    queryFn: async ({ signal }) => {
      const { data, error, response } = await api.GET('/api/bookings', { params: { query: { from, to } }, signal })
      if (!data) throw bookingError(error, response.status)
      return data
    },
  })

/** Every bookings window, for refetching after a change. */
export const BOOKINGS_KEY = ['bookings'] as const

/** Cancels the booking and deletes its calendar event. Cancelling one already cancelled is a no-op. */
export async function cancelBooking(id: string): Promise<BookingItem> {
  const { data, error, response } = await api.POST('/api/bookings/{booking_id}/cancel', {
    params: { path: { booking_id: id } },
  })
  if (!data) throw bookingError(error, response.status)
  return data
}

/** Moves the booking to start at `startsAt` (ISO 8601, with its offset). It keeps its length. */
export async function rescheduleBooking(id: string, startsAt: string): Promise<BookingItem> {
  const { data, error, response } = await api.POST('/api/bookings/{booking_id}/reschedule', {
    params: { path: { booking_id: id } },
    body: { starts_at: startsAt },
  })
  if (!data) throw bookingError(error, response.status)
  return data
}

export type CallSummary = components['schemas']['CallSummary']
export type CallList = components['schemas']['CallList']
export type CallDetail = components['schemas']['CallDetail']
export type CallTurn = components['schemas']['CallTurn']
export type CallMessage = components['schemas']['CallMessage']

/** How many calls one page of the log shows. */
export const CALLS_PAGE_SIZE = 25

/** A refused read of the call log. `code` is the API's reason. */
export class CallError extends Error {
  readonly status: number
  readonly code?: string

  constructor(message: string, status: number, code?: string) {
    super(message)
    this.status = status
    this.code = code
  }
}

/** What the person can do about each refusal, in words rather than codes. */
export function callErrorMessage(code: string | undefined): string {
  switch (code) {
    case 'call_not_found':
      return 'This call no longer exists. Calls are deleted once they pass the retention window.'
    case 'message_not_found':
      return 'This message no longer exists.'
    case 'role_cannot_handle_messages':
      return 'Only an owner or admin can mark a message handled.'
    case 'no_recording':
      return 'No recording for this call.'
    case 'recording_unavailable':
      return 'The recording couldn’t be loaded.'
    case 'role_cannot_play_recordings':
      return 'Only an owner or admin can play recordings.'
    case 'email_not_verified':
      return 'Verify your email address first.'
    default:
      return 'Something went wrong. Please try again.'
  }
}

function callError(error: unknown, status: number) {
  const code = (error as { error?: { code?: string } } | undefined)?.error?.code
  return new CallError(callErrorMessage(code), status, code)
}

/** Every page and filter of the call log, for refetching after a change. */
export const CALLS_KEY = ['calls'] as const

/**
 * The account's calls, one page at a time; only one agent's when `agent` names its slug,
 * and only those needing attention (urgent first) with `attention`. Each carries its
 * counts and badges, not its transcript.
 */
export const callsQuery = ({ agent, offset, attention = false }: { agent?: string; offset: number; attention?: boolean }) =>
  queryOptions({
    queryKey: ['calls', agent ?? '', offset, attention],
    queryFn: async ({ signal }) => {
      const { data, error, response } = await api.GET('/api/calls', {
        params: { query: { agent: agent || undefined, attention: attention || undefined, limit: CALLS_PAGE_SIZE, offset } },
        signal,
      })
      if (!data) throw callError(error, response.status)
      return data
    },
    placeholderData: keepPreviousData,
  })

/** One call with its transcript, oldest turn first. Each read is logged by the API. */
export const callQuery = (id: string) =>
  queryOptions({
    queryKey: ['call', id],
    queryFn: async ({ signal }) => {
      const { data, error, response } = await api.GET('/api/calls/{call_id}', {
        params: { path: { call_id: id } },
        signal,
      })
      if (!data) throw callError(error, response.status)
      return data
    },
  })

/** Marks a message handled: someone has called back. Marking it again changes nothing. */
export async function markMessageHandled(callId: string, messageId: string): Promise<CallMessage> {
  const { data, error, response } = await api.POST('/api/calls/{call_id}/messages/{message_id}/handled', {
    params: { path: { call_id: callId, message_id: messageId } },
  })
  if (!data) throw callError(error, response.status)
  return data
}

/** A call's recording and when it began, which places each turn in it (owner and admins only). */
export async function fetchCallRecording(
  callId: string,
  signal?: AbortSignal,
): Promise<{ audio: Blob; startedAt: Date }> {
  const { data, error, response } = await api.GET('/api/calls/{call_id}/recording', {
    params: { path: { call_id: callId } },
    parseAs: 'blob',
    signal,
  })
  const started = response.headers.get('X-Recording-Started-At')
  if (!data || !started) throw callError(error, response.status)
  return { audio: data, startedAt: new Date(started) }
}

export type CalendarConnection = components['schemas']['CalendarConnection']

/** The calendar the account books into: which, connected by whom, and whether the agent can book. */
export const calendarQuery = queryOptions({
  queryKey: ['calendar'],
  queryFn: async () => {
    const { data, error } = await api.GET('/api/calendar')
    if (error || !data) throw new Error('Could not load the calendar')
    return data
  },
})

/** The calendar and every bookings window: both change when a calendar is connected or disconnected. */
export const CALENDAR_KEY = ['calendar'] as const

/** A refused connect or disconnect, in words a person can act on. */
export class CalendarError extends Error {
  readonly code?: string

  constructor(message: string, code?: string) {
    super(message)
    this.code = code
  }
}

export function calendarErrorMessage(code: string | undefined): string {
  switch (code) {
    case 'role_cannot_manage_calendar':
      return 'Only an owner or admin can connect or disconnect the calendar.'
    case 'google_calendar_unconfigured':
      return 'Connecting Google Calendar isn’t set up on this server yet.'
    case 'email_not_verified':
      return 'Verify your email address first.'
    default:
      return 'Something went wrong. Please try again.'
  }
}

/**
 * Starts connecting Google Calendar: the API answers Google's consent page, and the
 * browser goes there (a full navigation: Google sends it back to the API, which sends
 * it back to `returnTo` with `?calendar=…`).
 */
export async function connectGoogleCalendar(returnTo: string): Promise<void> {
  const { data, error } = await api.POST('/api/calendar/google/connect', { body: { return_to: returnTo } })
  if (!data) {
    const code = (error as { error?: { code?: string } } | undefined)?.error?.code
    throw new CalendarError(calendarErrorMessage(code), code)
  }
  navigateTo(data.url)
}

/** Disconnects the calendar: the agent stops booking; bookings and their events stay. */
export async function disconnectCalendar(): Promise<void> {
  const { error, response } = await api.DELETE('/api/calendar')
  if (!response.ok) {
    const code = (error as { error?: { code?: string } } | undefined)?.error?.code
    throw new CalendarError(calendarErrorMessage(code), code)
  }
}

/** What `?calendar=` says when Google's consent sends the browser back, and whether it worked. */
export function calendarReturnMessage(marker: string): { ok: boolean; text: string } {
  switch (marker) {
    case 'connected':
      return { ok: true, text: 'Google Calendar connected. Your agent books into it from now on.' }
    case 'denied':
      return { ok: false, text: 'Google Calendar wasn’t connected: access wasn’t allowed in Google.' }
    case 'missing_scope':
      return {
        ok: false,
        text: 'Google Calendar wasn’t connected: allow both calendar permissions in Google, then try again.',
      }
    case 'expired':
      return { ok: false, text: 'That connection attempt expired. Start it again from RingOut.' }
    case 'signed_out':
      return { ok: false, text: 'You were signed out while connecting. Sign in and try again.' }
    case 'wrong_user':
      return { ok: false, text: 'That connection was started by someone else, so it wasn’t used. Start it again.' }
    case 'forbidden':
      return { ok: false, text: 'Only an owner or admin can connect the calendar.' }
    default:
      return { ok: false, text: 'Google Calendar couldn’t be connected. Please try again.' }
  }
}

export async function logout() {
  await api.POST('/api/auth/logout')
}

/** Full page navigation, not fetch: the browser must follow Google's redirect. */
export function startGoogleSignIn(next?: string, checkoutSessionId?: string, plan?: PlanKey) {
  const query = new URLSearchParams()
  if (next) query.set('next', next)
  // The plan chosen on the website: the new account keeps it on the free tier.
  if (plan) query.set('plan', plan)
  // The checkout the person has just paid for: the API keeps it across Google's round trip and
  // attaches the plan to the account that trip creates.
  if (checkoutSessionId) query.set('checkout_session_id', checkoutSessionId)
  const to = query.size ? `?${query.toString()}` : ''
  window.location.assign(apiUrl(`/api/auth/google/start${to}`))
}


/** Why a claim was refused, in words a person can act on. */
export function claimErrorMessage(code: string | undefined): string {
  switch (code) {
    case 'checkout_not_paid':
      return 'We couldn’t find a completed payment for that checkout. If you were charged, contact us and we’ll sort it out.'
    case 'checkout_already_used':
      return 'That payment is already attached to an account.'
    case 'already_subscribed':
      return 'This account already has a plan, so that payment wasn’t attached. Contact us if you were charged twice.'
    case 'role_cannot_manage_billing':
      return 'Only an owner or admin can attach a plan to this workspace.'
    case 'billing_unavailable':
    case 'billing_not_configured':
      return 'We couldn’t reach the payment provider just now. Your payment is safe; try again in a minute.'
    default:
      return 'We couldn’t attach that payment to your account.'
  }
}

/** Attach the plan a just-paid checkout bought to the account the person is signed in to. */
export async function claimCheckout(checkoutSessionId: string) {
  const { data, error } = await api.POST('/api/billing/claim', {
    body: { checkout_session_id: checkoutSessionId },
  })
  if (!data) {
    const code = (error as { error?: { code?: string } } | undefined)?.error?.code
    throw new AuthError(claimErrorMessage(code), code)
  }
  return data
}

/** Where the account's plan stands: its trial, and whether it is live. One per tenant. */
export const billingQuery = queryOptions({
  queryKey: ['billing'],
  queryFn: async () => {
    const { data, error } = await api.GET('/api/billing')
    if (error || !data) throw new Error('Could not load your plan')
    return data
  },
  staleTime: 60_000,
})

/** Start paying for a plan: the API creates the hosted checkout and redirects the browser to it. */
export const checkoutPath = (plan: PlanKey) =>
  apiUrl(`/api/checkout/start?plan=${encodeURIComponent(plan)}`)

export type BillingStatus = components['schemas']['BillingStatus']
export type PublicPlans = components['schemas']['PublicPlans']
export type PublicPlan = components['schemas']['PublicPlan']
export type PublicTax = components['schemas']['PublicTax']
export type BillingInterval = components['schemas']['PaymentSetupRequest']['interval']
export type PaymentSetup = components['schemas']['PaymentSetupResponse']
export type Subscribed = components['schemas']['Subscribed']
export type CardSummary = components['schemas']['CardSummary']

/** A refused payment setup or subscribe. `param` names the field (`plan`, `interval`, `setup_id`), when there is one. */
export class BillingSetupError extends Error {
  readonly code?: string
  readonly param?: string

  constructor(message: string, code?: string, param?: string) {
    super(message)
    this.code = code
    this.param = param
  }
}

/**
 * What the person can do about each refusal of the card form or the subscription. For a declined
 * card, `raw` is the payment provider's own words for the person, which it documents as safe to show.
 */
export function billingErrorMessage(code: string | undefined, raw?: string): string {
  switch (code) {
    case 'card_declined':
      return `${raw || 'Your card was declined.'} Nothing was charged; try again, or use another card.`
    case 'role_cannot_manage_billing':
      return 'Only an owner or admin can add a card for this workspace.'
    case 'payments_unconfigured':
      return 'Card payments aren’t switched on yet. You can add a card later in Billing.'
    case 'plan_unknown':
      return 'That plan isn’t sold in your region. Choose another plan.'
    case 'interval_unavailable':
      return 'That plan can’t be paid that way yet. Choose another billing period.'
    case 'already_subscribed':
      return 'Your workspace already has a card on file.'
    case 'setup_mismatch':
    case 'setup_not_yours':
    case 'setup_already_used':
      return 'Your card form had expired, so we’ve opened a new one. Try again.'
    case 'setup_incomplete':
      return 'Your bank didn’t confirm the card. Try again, or use another card.'
    case 'billing_unavailable':
      return 'We couldn’t reach the payment provider just now. Nothing was charged; try again in a minute.'
    default:
      return 'We couldn’t save your card. Nothing was charged; please try again.'
  }
}

function billingError(error: unknown) {
  const e = (error as { error?: { code?: string; param?: string; message?: string } } | undefined)?.error
  return new BillingSetupError(billingErrorMessage(e?.code, e?.message), e?.code, e?.param)
}

/** Opens the payment provider's card form for a plan: the browser saves the card there, never here. */
export async function startPaymentSetup(plan: PlanKey, interval: BillingInterval): Promise<PaymentSetup> {
  const { data, error } = await api.POST('/api/billing/payment-setup', { body: { plan, interval } })
  if (!data) throw billingError(error)
  return data
}

/**
 * One card form per plan and interval while the page is open: coming back to the step reuses
 * it, and another plan or interval opens its own, since the API refuses a setup for a different one.
 */
export const paymentSetupQuery = (plan: PlanKey | undefined, interval: BillingInterval) =>
  queryOptions({
    queryKey: ['payment-setup', plan ?? null, interval],
    queryFn: plan ? () => startPaymentSetup(plan, interval) : skipToken,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

/** Starts the plan's trial on the card a confirmed setup saved. */
export async function subscribe(setupId: string, plan: PlanKey, interval: BillingInterval): Promise<Subscribed> {
  const { data, error } = await api.POST('/api/billing/subscribe', { body: { setup_id: setupId, plan, interval } })
  if (!data) throw billingError(error)
  return data
}

/** The plans sold in a region (the server's own when none is named), priced ex-tax. */
export const plansQuery = (region?: string) =>
  queryOptions({
    queryKey: ['plans', region ?? ''],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/plans', { params: { query: { region } } })
      if (error || !data) throw new Error('Could not load the plans')
      return data
    },
    staleTime: 5 * 60_000,
  })

export type OnboardingState = components['schemas']['OnboardingState']
export type Faq = components['schemas']['Faq']
export type Industry = components['schemas']['OnboardingBusinessRequest']['industry']
export type AnswerMode = components['schemas']['OnboardingBusinessRequest']['answer_mode']
export type NumberMode = components['schemas']['OnboardingNumberRequest']['mode']
export type ForwardWhen = NonNullable<components['schemas']['OnboardingNumberRequest']['forward_when']>
export type OnboardingAboutBody = components['schemas']['OnboardingAboutRequest']
export type OnboardingBusinessBody = components['schemas']['OnboardingBusinessRequest']
export type OnboardingAgentBody = components['schemas']['OnboardingAgentRequest']
export type OnboardingNumberBody = components['schemas']['OnboardingNumberRequest']
type ProgressRequest = components['schemas']['OnboardingProgressRequest']
/** Only the fields sent change (the API merges them), so each one is optional. */
export type OnboardingProgressBody = Partial<ProgressRequest>

/** Where the setup wizard stands: everything it has saved. Every onboarding write answers the same. */
export const onboardingQuery = queryOptions({
  queryKey: ['onboarding'],
  queryFn: async () => {
    const { data, error } = await api.GET('/api/onboarding')
    if (error || !data) throw new Error('Could not load your setup')
    return data
  },
})

/** A refused onboarding save. `param` names the field as a dotted path (`locations.1.suburb`), when there is one. */
export class OnboardingSaveError extends Error {
  readonly code?: string
  readonly param?: string

  constructor(message: string, code?: string, param?: string) {
    super(message)
    this.code = code
    this.param = param
  }
}

const MISSING: Record<string, string> = {
  given_name: 'Add your first name.',
  family_name: 'Add your last name.',
  name: 'Add your business name.',
  industry_other: 'Tell us your industry.',
  greeting: 'Add a short greeting.',
}

/** What the person can do about each refusal, in words rather than codes. */
export function onboardingErrorMessage(code: string | undefined, param = ''): string {
  switch (code) {
    case 'mobile_invalid':
      return 'Enter an Australian mobile like 0412 345 678.'
    case 'business_number_invalid':
      return 'That number doesn’t check out. Use the ABN (11 digits) or ACN (9 digits) on your ASIC record.'
    case 'website_invalid':
      return 'Enter a web address like yourbusiness.com.au.'
    case 'industry_other_required':
      return 'Tell us your industry.'
    case 'postcode_unknown':
      return 'We couldn’t place that postcode. Choose the suburb again.'
    case 'locality_unknown':
      return 'Choose the suburb from the list.'
    case 'voice_unknown':
      return 'Choose a voice from the list.'
    case 'area_unknown':
      return 'Choose an area from the list.'
    case 'phone_invalid':
      return 'Enter an Australian landline, mobile or 1300 number.'
    case 'number_invalid':
      return 'Pick one of the numbers.'
    case 'onboarding_incomplete':
      return 'Save your business details first.'
    case 'role_cannot_edit_profile':
      return 'Only an owner or admin can set up this workspace.'
    case 'email_not_verified':
      return 'Verify your email address first.'
    case 'string_too_short':
    case 'missing':
      if (MISSING[param]) return MISSING[param]
      if (/^faqs\.\d+\.question$/.test(param)) return 'Write the question.'
      if (/^faqs\.\d+\.answer$/.test(param)) return 'Write the answer.'
      return 'Please fill this in.'
    case 'string_too_long':
      if (param === 'about') return 'Keep this to 300 characters.'
      return 'That’s too long. Shorten it and try again.'
    case 'literal_error':
      return 'Please choose one.'
    case 'too_short':
      return param === 'locations' ? 'Add at least one location.' : 'Please add one.'
    case 'too_long':
      if (param === 'locations') return 'Add up to 10 locations.'
      if (param === 'faqs') return 'Miya can know up to 50 answers.'
      if (param === 'services') return 'Miya can know up to 30 services.'
      if (param === 'areas') return 'Miya can cover up to 30 suburbs.'
      return 'That’s too many.'
    case 'greater_than_equal':
    case 'less_than_equal':
      return 'Choose a length from 15 minutes to 8 hours.'
    case 'value_error':
      // The script's rules across fields, reported on the list (distinct services, a day that can be kept).
      if (param === 'services') return 'Each service needs its own name.'
      if (param.startsWith('hours')) return 'The second window must start after the first one closes.'
      return 'Check this and try again.'
    default:
      return 'Something went wrong. Please try again.'
  }
}

function onboardingError(error: unknown) {
  const e = (error as { error?: { code?: string; param?: string } } | undefined)?.error
  return new OnboardingSaveError(onboardingErrorMessage(e?.code, e?.param), e?.code, e?.param)
}

/** Step 1: the person's name and mobile. The email is the sign-in address, never sent. */
export async function saveOnboardingAbout(body: OnboardingAboutBody): Promise<OnboardingState> {
  const { data, error } = await api.PUT('/api/onboarding/about', { body })
  if (!data) throw onboardingError(error)
  return data
}

/** Step 2: the business. Saving it onboards the account, which unlocks the dashboard. */
export async function saveOnboardingBusiness(body: OnboardingBusinessBody): Promise<OnboardingState> {
  const { data, error } = await api.PUT('/api/onboarding/business', { body })
  if (!data) throw onboardingError(error)
  return data
}

/** Step 3: the first agent's voice, greeting, toggles and answers; made as Miya when there is none. */
export async function saveOnboardingAgent(body: OnboardingAgentBody): Promise<OnboardingState> {
  const { data, error } = await api.PUT('/api/onboarding/agent', { body })
  if (!data) throw onboardingError(error)
  return data
}

export type AvailableNumber = components['schemas']['AvailableNumber']

/**
 * Step 4: local numbers that could be this business's, in an area (never a mobile). Nothing
 * holds them, so a list is kept only a minute. Throws `OnboardingSaveError`: `numbers_unavailable`
 * when there is no telephony provider or it failed.
 */
export const availableNumbersQuery = (area: string) =>
  queryOptions({
    queryKey: ['onboarding-numbers', area],
    queryFn: async ({ signal }) => {
      const { data, error } = await api.GET('/api/onboarding/numbers', {
        params: { query: { area } },
        signal,
      })
      if (!data) throw onboardingError(error)
      return data.numbers
    },
    staleTime: 60_000,
    retry: false,
  })

/** Step 4: the number choice. Saved only: no number is reserved yet. */
export async function saveOnboardingNumber(body: OnboardingNumberBody): Promise<OnboardingState> {
  const { data, error } = await api.PUT('/api/onboarding/number', { body })
  if (!data) throw onboardingError(error)
  return data
}

/** Where the wizard stands: its step, and what was tested, skipped or chosen. */
export async function saveOnboardingProgress(body: OnboardingProgressBody): Promise<OnboardingState> {
  // The generated type marks the defaulted booleans required; the API changes only what is sent.
  const { data, error } = await api.PUT('/api/onboarding/progress', { body: body as ProgressRequest })
  if (!data) throw onboardingError(error)
  return data
}
